import React, { useState, useEffect, useCallback } from 'react';
import { jobClientsAPI, usersAPI, jobVerticalAPI, jobApiError } from '../services/api';
import { useOpenParam } from '../utils/openParam';
import JobFeesPanel from './JobFeesPanel';
import ClientJourneyPanel from './ClientJourneyPanel';
import ClientStepsPanel from './ClientStepsPanel';
import LessonsPanel from './LessonsPanel';
import {
  FiRefreshCw, FiAlertCircle, FiPlus, FiX, FiEdit2, FiSearch,
} from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

// Stages of a client recruited to work abroad, in order (mirrors
// job_clients.models.JobClient.STATUS_CHOICES). The hint is what "done"
// means for that stage, shown under the Status dropdown.
const STATUSES = [
  ['NEW', 'New enquiry', '#011ff3', 'Enquiry received — not spoken to yet.'],
  ['CONTACTED', 'Consultation done', '#8b5cf6', 'Spoken to them: destination, type of job, experience and timeline confirmed.'],
  ['DOCS_READY', 'CV & documents ready', '#a855f7', 'CV updated; passport (valid well beyond the contract start), diplomas/certificates, police clearance and reference letters collected and checked.'],
  ['MATCHING', 'Presented to employers', '#d946ef', 'Profile sent to employers; interviews being arranged or done.'],
  ['OFFER_SIGNED', 'Offer & contract signed', '#ec4899', 'Written contract received, explained to the client in a language they understand, and signed. Check it matches the offer (salary, hours, accommodation) — no swapped contracts.'],
  ['PERMIT_APPLIED', 'Work permit applied', '#f59e0b', 'Client lodged at the consulate/visa centre and the employer filed the work permit. Türkiye: client gets a 16-digit reference number; employer must file online within 10 working days.'],
  ['PERMIT_APPROVED', 'Work permit approved', '#eab308', 'Permit approved by the labour ministry/immigration; employer has paid the permit fees.'],
  ['VISA_ISSUED', 'Work visa issued', '#84cc16', 'Work visa stamped in the passport. Note the latest date they must enter the country.'],
  ['PRE_DEPARTURE', 'Ready to travel', '#22c55e', 'Flight booked; pre-departure briefing done (contract & rights, employer contact, accommodation, what happens on arrival).'],
  ['ARRIVED', 'Arrived & settled', '#14b8a6', 'Landed, met, moved into accommodation; local address registered.'],
  ['PLACED', 'Started work', '#10b981', 'Started with the employer. Follow up after 30 and 90 days.'],
  ['CANCELLED', 'Cancelled / withdrawn', '#f5576c', 'Client withdrew, or the offer/permit fell through. Record why in Notes.'],
];

const statusColor = (s) => (STATUSES.find((x) => x[0] === s) || [null, null, '#999'])[2];
const statusLabel = (s) => (STATUSES.find((x) => x[0] === s) || [null, s])[1];
const statusHint = (s) => (STATUSES.find((x) => x[0] === s) || [])[3] || '';
// "Step 5 of 11" — CANCELLED is an outcome, not a step on the journey.
const JOURNEY = STATUSES.filter((x) => x[0] !== 'CANCELLED');
const stepNo = (s) => JOURNEY.findIndex((x) => x[0] === s) + 1; // 0 = not a step
const statusStep = (s) => (stepNo(s) ? `Step ${stepNo(s)} of ${JOURNEY.length}` : '');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

// Everything staff need before (and after) a worker is presented to an
// employer: verification checklist, per-employer consent, the destination's
// permit-rule guidance (warnings only), check-ins and at-cost expenses.
const ReadinessPanel = ({ client, employers }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState({ employer: '', method: '' });
  const [expense, setExpense] = useState({ description: '', amount: '', currency: 'USD', paid_to: '', receipt_reference: '' });
  const [checkinForm, setCheckinForm] = useState({});
  const [schedule, setSchedule] = useState([]);
  useEffect(() => {
    jobVerticalAPI.feeSchedule.list().then((r) => setSchedule(r.data.results || r.data || [])).catch(() => setSchedule([]));
  }, []);

  const reload = useCallback(async () => {
    try {
      const r = await jobClientsAPI.readiness(client.id);
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr(jobApiError(e, 'Could not load readiness.'));
    }
  }, [client.id]);
  useEffect(() => { reload(); }, [reload]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); setErr(''); await reload(); } catch (e) { setErr(jobApiError(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="cv-subsection"><div className="cv-subsection-empty">{err || 'Loading readiness…'}</div></div>;

  const usable = employers.filter((e) => e.status !== 'SUSPENDED' && String(e.country) === String(client.destination));
  const warnColor = { warning: '#92400e', info: '#1e40af' };
  const warnBg = { warning: '#fef3c7', info: '#dbeafe' };

  return (
    <div style={{ padding: '0 20px 8px' }}>
      {err && <div className="error-message" style={{ marginTop: 12 }}>{err}</div>}

      {data.guidance.length > 0 && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Permit rules — guidance only, does not block</span></div>
          {data.guidance.map((n, i) => (
            <div key={i} style={{ background: warnBg[n.severity] || '#f1f5f9', color: warnColor[n.severity] || '#334155', borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12, lineHeight: 1.45 }}>
              <strong>{n.title}</strong>{n.valid_until ? ` (until ${n.valid_until})` : ''}<div>{n.text}</div>
            </div>
          ))}
        </div>
      )}

      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Verification checklist</span></div>
        {!client.destination && <div className="cv-subsection-empty">Choose a destination and save to see the checklist.</div>}
        {data.checklist.map((c) => (
          <div key={c.requirement} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: c.verified ? '#15803d' : '#b91c1c', fontWeight: 700, width: 16 }}>{c.verified ? '✓' : '○'}</span>
            <span style={{ flex: 1 }}>{c.label}
              {c.verified && <span style={{ color: '#94a3b8', fontSize: 11 }}> — {c.verified_by || 'staff'}, {fmtDate(c.verified_at)}</span>}
            </span>
            {c.verified
              ? <button className="btn-secondary" disabled={busy} onClick={() => run(() => jobVerticalAPI.checks.delete(c.check_id))}>Undo</button>
              : <button className="btn-primary" disabled={busy} onClick={() => run(() => jobVerticalAPI.checks.create({ job_client: client.id, requirement: c.requirement }))}>Mark checked</button>}
          </div>
        ))}
        {data.blockers.length > 0 ? (
          <div style={{ marginTop: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
            <strong>Cannot be presented to employers yet:</strong>
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{data.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        ) : (
          <div style={{ marginTop: 8, background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>Verified, with consent on file — clear to present.</div>
        )}
      </div>

      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Consent to share CV (per employer)</span></div>
        {data.consents.length === 0 && <div className="cv-subsection-empty">No consent recorded.</div>}
        {data.consents.map((c) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
            <span style={{ flex: 1 }}>
              <strong>{c.employer_name}</strong> — {c.method}, {fmtDate(c.consented_at)}
              {c.withdrawn_at && <span style={{ color: '#b91c1c' }}> · withdrawn {fmtDate(c.withdrawn_at)}</span>}
              {c.disclosed_at && <span style={{ color: '#15803d' }}> · CV shared {fmtDate(c.disclosed_at)}</span>}
            </span>
            {c.is_active && !c.disclosed_at && <button className="btn-secondary" disabled={busy} onClick={() => run(() => jobVerticalAPI.consents.disclose(c.id))}>Mark CV shared</button>}
            {c.is_active && <button className="btn-secondary" disabled={busy} onClick={() => run(() => jobVerticalAPI.consents.withdraw(c.id))}>Withdraw</button>}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <select value={consent.employer} onChange={(e) => setConsent((c) => ({ ...c, employer: e.target.value }))} style={{ flex: 1 }}>
            <option value="">Employer…</option>
            {usable.map((e) => <option key={e.id} value={e.id}>{e.name}{e.status !== 'VERIFIED' ? ' (unverified)' : ''}</option>)}
          </select>
          <input placeholder="How consent was given (signed form, voice note…)" value={consent.method}
            onChange={(e) => setConsent((c) => ({ ...c, method: e.target.value }))} style={{ flex: 2 }} />
          <button className="btn-primary" disabled={busy || !consent.employer || !consent.method.trim()}
            onClick={() => run(async () => {
              await jobVerticalAPI.consents.create({ job_client: client.id, employer: parseInt(consent.employer, 10), method: consent.method.trim() });
              setConsent({ employer: '', method: '' });
            })}>Record consent</button>
        </div>
      </div>

      {data.checkins.length > 0 && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Aftercare check-ins</span></div>
          {data.checkins.map((c) => {
            const f = checkinForm[c.id] || { outcome: '', ok: 'true' };
            return (
              <div key={c.id} style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px solid #e2e8f0' }}>
                <strong>Day {c.day}</strong> — due {fmtDate(c.due_date)} — {c.status === 'DONE'
                  ? <span style={{ color: c.worker_ok ? '#15803d' : '#b91c1c' }}>{c.worker_ok ? 'worker OK' : 'WORKER NOT OK'}: {c.outcome}</span>
                  : <span style={{ color: '#b45309' }}>due</span>}
                {c.status !== 'DONE' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <input placeholder="What the worker said (paid? housed? safe?)" value={f.outcome} style={{ flex: 1 }}
                      onChange={(e) => setCheckinForm((m) => ({ ...m, [c.id]: { ...f, outcome: e.target.value } }))} />
                    <select value={f.ok} onChange={(e) => setCheckinForm((m) => ({ ...m, [c.id]: { ...f, ok: e.target.value } }))}>
                      <option value="true">Worker is OK</option>
                      <option value="false">Worker NOT OK</option>
                    </select>
                    <button className="btn-primary" disabled={busy || !f.outcome.trim()}
                      onClick={() => run(() => jobVerticalAPI.checkins.complete(c.id, { outcome: f.outcome.trim(), worker_ok: f.ok === 'true' }))}>Log call</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <JobFeesPanel client={client} charges={data.charges || []} schedule={schedule} onChanged={reload} />

      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Third-party costs paid at cost (visa fee, travel) — not our fees</span></div>
        {data.expenses.length === 0 && <div className="cv-subsection-empty">None recorded.</div>}
        {data.expenses.map((x) => (
          <div key={x.id} style={{ fontSize: 13, padding: '3px 0' }}>{x.description} — {x.amount} {x.currency} — receipt {x.receipt_reference}{x.paid_to ? ` — paid to ${x.paid_to}` : ''}</div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1.5fr auto', gap: 6, marginTop: 8 }}>
          <input placeholder="e.g. Work visa fee" value={expense.description} onChange={(e) => setExpense((x) => ({ ...x, description: e.target.value }))} />
          <input placeholder="Amount" type="number" min="0" value={expense.amount} onChange={(e) => setExpense((x) => ({ ...x, amount: e.target.value }))} />
          <input placeholder="Cur." maxLength={3} value={expense.currency} onChange={(e) => setExpense((x) => ({ ...x, currency: e.target.value.toUpperCase() }))} />
          <input placeholder="Paid to (consulate…)" value={expense.paid_to} onChange={(e) => setExpense((x) => ({ ...x, paid_to: e.target.value }))} />
          <input placeholder="Receipt ref." value={expense.receipt_reference} onChange={(e) => setExpense((x) => ({ ...x, receipt_reference: e.target.value }))} />
          <button className="btn-primary" disabled={busy || !expense.description || !expense.amount || !expense.receipt_reference}
            onClick={() => run(async () => {
              await jobVerticalAPI.expenses.create({ ...expense, job_client: client.id });
              setExpense({ description: '', amount: '', currency: expense.currency, paid_to: '', receipt_reference: '' });
            })}>Add</button>
        </div>
      </div>
    </div>
  );
};

const ClientModal = ({ initial, agents, destinations, employers, onSave, onCancel, loading, saveError }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    status: initial?.status || 'NEW',
    assigned_agent: initial?.assigned_agent || '',
    current_role: initial?.current_role || '',
    desired_role: initial?.desired_role || '',
    industry: initial?.industry || '',
    years_of_experience: initial?.years_of_experience ?? '',
    destination_country: initial?.destination_country || '',
    destination: initial?.destination || '',
    job_type: initial?.job_type || 'OTHER',
    country: initial?.country || '',
    linkedin_url: initial?.linkedin_url || '',
    notes: initial?.notes || '',
  });
  const [collaboratorIds, setCollaboratorIds] = useState(
    (initial?.assigned_agents_detail || []).map((a) => String(a.id))
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCollaborator = (id) => setCollaboratorIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );
  const dest = destinations.find((d) => String(d.id) === String(form.destination));
  // Stages come from the destination's own pipeline once one is chosen; the
  // built-in list is only the fallback before a destination is picked.
  const stageOptions = dest && dest.stages && dest.stages.length
    ? dest.stages.map((st) => {
        const base = STATUSES.find((x) => x[0] === st.code) || [st.code, st.label];
        return [st.code, st.label || base[1]];
      })
    : STATUSES.map(([v, l]) => [v, l]);
  const canSave = form.first_name.trim() && form.last_name.trim() && form.email.trim() && !loading;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div>
            <h2>{isEdit ? 'Edit Job Seeker' : 'New Job Seeker'}</h2>
            {isEdit && <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>{initial?.job_id}</div>}
          </div>
          <button className="modal-close" onClick={onCancel}><FiX /></button>
        </div>

        <div className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                {stageOptions.map(([v, l]) => (
                  <option key={v} value={v}>{stepNo(v) ? `${stepNo(v)}. ` : ''}{l}</option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
                {statusStep(form.status) && <strong>{statusStep(form.status)}: </strong>}
                {statusHint(form.status)}
              </div>
            </div>
            <div className="form-group">
              <label>Assigned Agent</label>
              <select value={form.assigned_agent} onChange={(e) => set('assigned_agent', e.target.value)}>
                <option value="">— none —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.displayName || a.username}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Also assign (co-workers who can also see this record)</label>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 8, maxHeight: 130, overflowY: 'auto' }}>
              {agents.filter((a) => String(a.id) !== String(form.assigned_agent)).map((a) => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" style={{ width: 'auto' }}
                    checked={collaboratorIds.includes(String(a.id))}
                    onChange={() => toggleCollaborator(String(a.id))} />
                  {a.displayName || a.username}
                </label>
              ))}
              {agents.length === 0 && <div style={{ color: '#999', fontSize: 12 }}>No agents/employees to add.</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Current Role</label>
              <input value={form.current_role} onChange={(e) => set('current_role', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Desired Role</label>
              <input value={form.desired_role} onChange={(e) => set('desired_role', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Industry</label>
              <input value={form.industry} onChange={(e) => set('industry', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Years of Experience</label>
              <input type="number" min="0" value={form.years_of_experience} onChange={(e) => set('years_of_experience', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Destination</label>
              <select value={form.destination} onChange={(e) => {
                const d = destinations.find((x) => String(x.id) === e.target.value);
                setForm((f) => ({ ...f, destination: e.target.value, destination_country: d ? d.name : f.destination_country }));
              }}>
                <option value="">— choose a destination —</option>
                {destinations.filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Job type</label>
              <select value={form.job_type} onChange={(e) => set('job_type', e.target.value)}>
                <option value="FACTORY">Factory worker</option>
                <option value="CAREGIVER">Caregiver (child / elderly / patient care)</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Destination (as typed)</label>
              <input value={form.destination_country} onChange={(e) => set('destination_country', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Home Country</label>
              <input value={form.country} onChange={(e) => set('country', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>LinkedIn URL</label>
            <input value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
          </div>
        </div>

        {isEdit && <ClientJourneyPanel kind="job" clientId={initial.id} />}
        {isEdit && <ClientStepsPanel kind="JOB" clientId={initial.id} leadSource={initial.lead_source} leadPaid={initial.lead_paid} />}
        {isEdit && <LessonsPanel kind="job" clientId={initial.id} />}
        {isEdit && <ReadinessPanel client={initial} employers={employers} />}

        {saveError && (
          <div className="error-message" style={{ margin: '12px 20px 0' }}>{saveError}</div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canSave}
            onClick={() => onSave({
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              email: form.email.trim(),
              phone: form.phone || '',
              status: form.status,
              assigned_agent: form.assigned_agent ? parseInt(form.assigned_agent, 10) : null,
              assigned_agent_ids: collaboratorIds.map((id) => parseInt(id, 10)),
              current_role: form.current_role || '',
              desired_role: form.desired_role || '',
              industry: form.industry || '',
              years_of_experience: form.years_of_experience === '' ? null : form.years_of_experience,
              destination_country: form.destination_country || '',
              destination: form.destination ? parseInt(form.destination, 10) : null,
              job_type: form.job_type,
              country: form.country || '',
              linkedin_url: form.linkedin_url || '',
              notes: form.notes || '',
            })}
          >
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Job Seeker'}
          </button>
        </div>
      </div>
    </div>
  );
};

const JobClients = () => {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  useOpenParam('client', (id) => jobClientsAPI.get(id).then((r) => setModal(r.data)).catch(() => {}));
  const [actionLoading, setActionLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [employers, setEmployers] = useState([]);

  useEffect(() => {
    jobVerticalAPI.destinations.list().then((r) => setDestinations(r.data.results || r.data || [])).catch(() => setDestinations([]));
    jobVerticalAPI.employers.list().then((r) => setEmployers(r.data.results || r.data || [])).catch(() => setEmployers([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ordering: '-created_at' };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await jobClientsAPI.list(params);
      setClients(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load job seekers.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  const loadAgents = useCallback(async () => {
    try {
      const [agentsRes, employeesRes] = await Promise.all([
        usersAPI.agents(),
        usersAPI.permanentEmployees().catch(() => ({ data: [] })),
      ]);
      const agentsData = agentsRes.data || [];
      const employeesData = employeesRes.data || [];
      setAgents([
        ...agentsData.map((a) => ({ ...a, displayName: `${a.username} (Agent)` })),
        ...employeesData.map((e) => ({ ...e, displayName: `${e.username} (Employee)` })),
      ]);
    } catch {
      setAgents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleSave = async (data) => {
    setActionLoading(true);
    try {
      if (modal?.id) await jobClientsAPI.update(modal.id, data);
      else await jobClientsAPI.create(data);
      setModal(null);
      setSaveError('');
      load();
    } catch (e) {
      // Stay in the modal so the gate's reasons ("Not yet verified: …") are readable next to the form.
      setSaveError(jobApiError(e, 'Failed to save job seeker.'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="students-page">
      <div className="page-header">
        <div className="header-actions-left">
          <button className="btn-primary" onClick={() => { setSaveError(''); setModal({}); }}>
            <FiPlus /> New Job Seeker
          </button>
        </div>
        <div className="header-actions-right">
          <div className="search-box">
            <FiSearch />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, email, phone, job ID…" />
          </div>
          <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertCircle size={16} />
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={14} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">No job seekers found.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Desired Role</th>
                <th>Agent</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} onClick={() => { setSaveError(''); setModal(c); }} style={{ cursor: 'pointer' }}>
                  <td><span className="cv-id">{c.job_id}</span></td>
                  <td>
                    <strong>{c.full_name || `${c.first_name} ${c.last_name}`}</strong>
                    <div style={{ color: '#999', fontSize: 12 }}>{c.email}</div>
                  </td>
                  <td>
                    <span className="status-pill" style={{ color: statusColor(c.status) }}>
                      {c.status_display || statusLabel(c.status)}
                    </span>
                    {statusStep(c.status) && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{statusStep(c.status)}</div>
                    )}
                  </td>
                  <td>{c.desired_role || '—'}{c.destination_name && <div style={{ color: '#999', fontSize: 12 }}>{c.destination_name}</div>}</td>
                  <td>{c.assigned_agent_name || '—'}</td>
                  <td>{fmtDate(c.created_at)}</td>
                  <td>
                    <button className="btn-icon" onClick={(e) => { e.stopPropagation(); setModal(c); }} title="Edit">
                      <FiEdit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ClientModal
          agents={agents}
          destinations={destinations}
          employers={employers}
          initial={modal.id ? modal : null}
          onSave={handleSave}
          onCancel={() => { setModal(null); setSaveError(''); }}
          loading={actionLoading}
          saveError={saveError}
        />
      )}
    </div>
  );
};

export default JobClients;
